import Vue from "vue";
import {
  ICompletion,
  ICompletionObject,
  ICompletionQuery,
  ICursorPosition, ICustomHintOptions,
  MentionWidget, WidgetType
} from "~/helpers/codeMirrorWidget";
import CodeMirror from "codemirror/lib/codemirror"

export default Vue.extend({
  data() {
    return {
      lastPickedCompletion: null,
    }
  },
  methods: {
    /**
     * The custom hinting function that show-hint.js relies on.
     * This is passed in as a config option when instantiating CodeMirror.
     * In order to support asynchronous functionality, this method must return a promise.
     * @see {@link https://codemirror.net/doc/manual.html#addon_show-hint}
     */
    customHintFunction(cm: CodeMirror, options: ICustomHintOptions): Promise<ICompletionObject> {
      const {query, from, to} = this.getQuery(cm)

      // color partial word and '@' blue as user types
      if (query.charAt(0) === '@') {
        cm.markText(CodeMirror.Pos(from.line, from.ch), to, {css: "color: #3876CC"})
      }

      return new Promise<ICompletionObject>(async (resolve, reject) => {
        resolve(await this.performSearch(query, from, to, options))
      })
    },
    /**
     * Grabs query and query cursor positions by traversing input backwards until the '@' symbol.
     */
    getQuery(cm: CodeMirror): ICompletionQuery {
      // get start and end cursor positions of query word
      const cursor: ICursorPosition = cm.getCursor()
      const lineText: string = cm.getLine(cursor.line)
      let start: number = cursor.ch
      let end: number = start
      while (start && !(/@/.test(lineText.charAt(start)))) { --start }
      while (end < lineText.length && /\w/.test(lineText.charAt(end))) { ++end }
      // get query word itself from cursor positions
      const from: ICursorPosition = CodeMirror.Pos(cursor.line, start)
      const to: ICursorPosition = CodeMirror.Pos(cursor.line, end)
      const query: string = cm.getRange(from, to)

      return {query, from, to}
    },
    /**
     * The call to the backend.
     * @param q Assumes first character is '@' and will remove it from query.
     */
    async performSearch(q: string, from: ICursorPosition, to: ICursorPosition, options: ICustomHintOptions): Promise<ICompletionObject> {
      q = q.slice(1) // remove '@'
      const list: ICompletion[] = this.formatResponse(
        await $.ajax({
          url: `/notes/search_users_for_mentions`,
          type: 'GET',
          dataType: 'json',
          data: {
            q: q,
            organization_id: options["organizationId"],
            application_id: options["applicationId"]
          }
        })
      )
      return this.generateCompletionObject(list, from, to)
    },

    /**
     * Format the server response to align with show-hint.js' expectations for the list of completions.
     */
    formatResponse(response): ICompletion[] {
      const list = []
      const users = response.users
      users.forEach(user => list.push({
        displayText: `${user.first_name} ${user.last_name}`,
        text: `@${user.id}`,
        render: MentionWidget.generateListItemEl,
        avatarId: user.avatar_id,
        initials: this.$options.filters.initials({first_name: user.first_name, last_name: user.last_name})
      }))
      return list
    },
    /**
     * The 'completion object' is the object expected by the show-hint.js popup.
     * It contains the list of completions and the cursor positions of the text to replace (i.e. the partially typed word).
     */
    generateCompletionObject(list: ICompletion[], from: ICursorPosition, to: ICursorPosition): ICompletionObject {
      const completionObject: ICompletionObject = {list, from, to}
      CodeMirror.on(completionObject, 'pick', this.pickCompletion)
      CodeMirror.on(completionObject, 'close', this.closePopup)
      return completionObject
    },
    /**
     * This event is fired when a completion is picked from the popup.
     * The picked completion is temporarily stored for use by the close popup handler.
     */
    pickCompletion(completion: ICompletion): void {
      this.lastPickedCompletion = completion
    },
    /**
     * This event is fired when the popup closes.
     */
    closePopup(): void {
      if (this.lastPickedCompletion !== null) {
        this.editor.addWidget(WidgetType.MentionWidget, this.lastPickedCompletion)
          .then(mentionWidget => mentionWidget.mark())
      }
      this.lastPickedCompletion = null
    },
  }
});
